import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PersonIcon from "@material-ui/icons/Person";
import TimerIcon from "@material-ui/icons/Timer";
import managerTestApi from "api/managerTest";
import { useAppSelector } from "app/hooks";
import { selectInfoLogin } from "features/Auth/authSlice";
import { DetailTestByUser, StudentAnswers } from "models/managerTest";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { scroller } from "react-scroll";
import { secondsToHms } from "utils/timeConvert";

export default function DetailResultTest() {
  const useStyles = makeStyles((theme: Theme) => ({
    container: {
      paddingTop: 28,
      paddingBottom: 48,
      [theme.breakpoints?.down("sm")]: {
        paddingTop: 16,
        paddingBottom: 24,
      },
    },
    backButton: {
      marginBottom: 20,
      color: "#475569",
      fontWeight: 700,
      fontSize: "0.875rem",
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 14px",
      backgroundColor: "#F8FAFC",
      border: "1px solid #E2E8F0",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "#F1F5F9",
        color: "#0F172A",
        transform: "translateX(-2px)",
      },
    },
    headerBanner: {
      background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      borderRadius: 24,
      padding: "32px 36px",
      color: "#FFFFFF",
      marginBottom: 28,
      position: "relative",
      overflow: "hidden",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.25)",
      [theme.breakpoints?.down("sm")]: {
        padding: "20px 18px",
        borderRadius: 16,
      },
    },
    headerGlow: {
      position: "absolute",
      top: "-40%",
      right: "-10%",
      width: 350,
      height: 350,
      background: "radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, rgba(99, 102, 241, 0) 70%)",
      pointerEvents: "none",
    },
    headerBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: "rgba(255, 255, 255, 0.12)",
      backdropFilter: "blur(8px)",
      padding: "4px 14px",
      borderRadius: 20,
      fontSize: "0.75rem",
      fontWeight: 800,
      color: "#818CF8",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      marginBottom: 12,
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },
    headerTitle: {
      fontSize: "1.75rem",
      fontWeight: 900,
      color: "#FFFFFF",
      marginBottom: 16,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "1.25rem",
      },
    },
    metricGroup: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      flexWrap: "wrap",
    },
    metricPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: "rgba(255, 255, 255, 0.1)",
      borderRadius: 12,
      padding: "8px 16px",
      fontSize: "0.875rem",
      fontWeight: 800,
      color: "#FFFFFF",
      border: "1px solid rgba(255, 255, 255, 0.15)",
    },

    // Question Card
    questionCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 20,
      padding: 28,
      border: "1px solid #E2E8F0",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.03)",
      marginBottom: 20,
      transition: "all 0.2s ease",
      [theme.breakpoints?.down("sm")]: {
        padding: 18,
        borderRadius: 16,
      },
    },
    questionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 14,
      gap: 12,
      flexWrap: "wrap",
    },
    questionBadge: {
      backgroundColor: "#EEF2FF",
      color: "#4F46E5",
      fontWeight: 900,
      fontSize: "0.825rem",
      padding: "4px 12px",
      borderRadius: 10,
      letterSpacing: "0.04em",
    },
    questionText: {
      fontSize: "1.1rem",
      fontWeight: 800,
      color: "#0F172A",
      lineHeight: 1.5,
      marginBottom: 18,
      [theme.breakpoints?.down("sm")]: {
        fontSize: "0.95rem",
      },
    },

    // Choice Card
    choiceItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 18px",
      borderRadius: 14,
      marginBottom: 10,
      border: "1.5px solid #E2E8F0",
      backgroundColor: "#F8FAFC",
      transition: "all 0.2s ease",
      "&:last-child": {
        marginBottom: 0,
      },
    },
    choiceItemCorrect: {
      backgroundColor: "#F0FDF4",
      borderColor: "#86EFAC",
    },
    choiceItemWrong: {
      backgroundColor: "#FEF2F2",
      borderColor: "#FCA5A5",
    },
    choiceText: {
      fontSize: "0.95rem",
      fontWeight: 700,
      color: "#334155",
      [theme.breakpoints?.down("sm")]: {
        fontSize: "0.85rem",
      },
    },

    // Scroll Control Buttons
    scrollControlBox: {
      position: "fixed",
      right: 28,
      bottom: 32,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      zIndex: 100,
      [theme.breakpoints?.down("sm")]: {
        right: 16,
        bottom: 20,
      },
    },
    scrollBtn: {
      width: 44,
      height: 44,
      borderRadius: "50%",
      backgroundColor: "#FFFFFF",
      color: "#4F46E5",
      boxShadow: "0 6px 20px rgba(0, 0, 0, 0.15)",
      border: "1px solid #E2E8F0",
      minWidth: 0,
      padding: 0,
      "&:hover": {
        backgroundColor: "#4F46E5",
        color: "#FFFFFF",
      },
    },
  }));

  const { t } = useTranslation();
  const classes = useStyles();
  const params = useParams();
  const location: any = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints?.down("sm"));
  const infoUser = useAppSelector(selectInfoLogin);
  const [detailTestByUser, setDetailTestByUser] = useState<DetailTestByUser>();

  useEffect(() => {
    (async () => {
      try {
        const res: DetailTestByUser = await managerTestApi.getDetailTestByUser(params?.id);
        if (res) {
          setDetailTestByUser(res);
        }
      } catch (err) {
        console.error("Failed to fetch detailTestByUser", err);
      }
    })();
  }, [params?.id]);

  const fetchedAnswers = detailTestByUser?.submissionDetails?.studentAnswers;
  const answersList: any[] = fetchedAnswers || [];

  const handleScrollToTop = () => {
    scroller.scrollTo("header-banner", {
      duration: 600,
      smooth: "easeInOutQuart",
      offset: -80,
    });
  };

  const handleScrollToBottom = () => {
    scroller.scrollTo("footer-actions", {
      duration: 600,
      smooth: "easeInOutQuart",
      offset: -80,
    });
  };

  const totalPoint = detailTestByUser?.submissionDetails?.point ?? 80;
  const examTitle = detailTestByUser?.submissionDetails?.exam?.examTitle || "🔥 [LÀM BÀI NGAY] Kiểm Tra Nhanh An Toàn Sinh Học GPET 2026";
  const durationSec = location?.state?.duration || 245;

  return (
    <Container maxWidth="md" className={classes.container}>
      <Button
        startIcon={<ArrowBackIcon />}
        className={classes.backButton}
        onClick={() => navigate(-1)}
      >
        Quay lại lịch sử thi
      </Button>

      <Box className={classes.headerBanner} id="header-banner">
        <Box className={classes.headerGlow} />
        <Box className={classes.headerBadge}>
          <HelpOutlineIcon style={{ fontSize: 16 }} />
          BẢNG ĐÁP ÁN & CHI TIẾT BÀI LÀM
        </Box>

        <Typography className={classes.headerTitle}>{examTitle}</Typography>

        <Box className={classes.metricGroup}>
          <Box className={classes.metricPill} style={{ backgroundColor: "rgba(16, 185, 129, 0.2)", borderColor: "rgba(16, 185, 129, 0.4)" }}>
            <EmojiEventsIcon style={{ color: "#34D399", fontSize: 18 }} />
            Điểm số: <span style={{ color: "#6EE7B7", fontSize: "1.05rem" }}>{totalPoint} điểm</span>
          </Box>

          <Box className={classes.metricPill}>
            <TimerIcon style={{ color: "#818CF8", fontSize: 18 }} />
            Thời gian: {secondsToHms(durationSec)}
          </Box>

          <Box className={classes.metricPill}>
            <PersonIcon style={{ color: "#93C5FD", fontSize: 18 }} />
            Thí sinh: {infoUser?.name || ""}
          </Box>
        </Box>
      </Box>

      {answersList.map((answer: StudentAnswers, index: number) => {
        const isUserCorrect = answer.isCorrect;

        return (
          <Paper key={answer.id || index} className={classes.questionCard} elevation={0}>
            <Box className={classes.questionHeader}>
              <Box className={classes.questionBadge}>
                {t("test.cau")} {index + 1} / {answersList.length}
              </Box>

              {isUserCorrect ? (
                <Chip
                  icon={<CheckCircleIcon style={{ color: "#16A34A" }} />}
                  label="CHÍNH XÁC"
                  size="small"
                  style={{ backgroundColor: "#F0FDF4", color: "#16A34A", fontWeight: 800 }}
                />
              ) : (
                <Chip
                  icon={<CancelIcon style={{ color: "#DC2626" }} />}
                  label="CHƯA CHÍNH XÁC"
                  size="small"
                  style={{ backgroundColor: "#FEF2F2", color: "#DC2626", fontWeight: 800 }}
                />
              )}
            </Box>

            <Typography className={classes.questionText}>
              {answer.question.content}
            </Typography>

            {answer.question.imageFileList?.length > 0 && (
              <Box
                display="grid"
                gridTemplateColumns={answer?.question?.imageFileList?.length === 1 ? "1fr" : "1fr 1fr"}
                gridGap={10}
                mb={2}
              >
                {answer.question.imageFileList.map((imgItem: any) => (
                  <img
                    key={imgItem.id}
                    style={{
                      width: "100%",
                      borderRadius: 12,
                      maxHeight: "350px",
                      objectFit: "contain",
                      border: "1px solid #E2E8F0",
                    }}
                    src={imgItem.imageFile.url}
                    alt="Question visual"
                  />
                ))}
              </Box>
            )}

            <Box>
              {answer.question.answers.map((item) => {
                const isSelectedByStudent = answer.choice && String(answer.choice) === String(item.id);
                const isRightChoice = Boolean(item.isRightAnswer);

                let choiceClass = classes.choiceItem;
                if (isRightChoice) {
                  choiceClass = `${classes.choiceItem} ${classes.choiceItemCorrect}`;
                } else if (isSelectedByStudent && !isRightChoice) {
                  choiceClass = `${classes.choiceItem} ${classes.choiceItemWrong}`;
                }

                return (
                  <Box key={item.id} className={choiceClass}>
                    <Typography
                      className={classes.choiceText}
                      style={{
                        fontWeight: isRightChoice || isSelectedByStudent ? 800 : 600,
                        color: isRightChoice
                          ? "#15803D"
                          : isSelectedByStudent
                          ? "#B91C1C"
                          : "#334155",
                      }}
                    >
                      {item?.content}
                    </Typography>

                    <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {isRightChoice && (
                        <Chip
                          icon={<CheckCircleIcon style={{ color: "#16A34A" }} />}
                          label="Đáp án đúng"
                          size="small"
                          style={{ backgroundColor: "#DCFCE7", color: "#15803D", fontWeight: 800, fontSize: "0.75rem" }}
                        />
                      )}
                      {isSelectedByStudent && !isRightChoice && (
                        <Chip
                          icon={<CancelIcon style={{ color: "#DC2626" }} />}
                          label="Lựa chọn của bạn"
                          size="small"
                          style={{ backgroundColor: "#FEE2E2", color: "#B91C1C", fontWeight: 800, fontSize: "0.75rem" }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}



      <Box className={classes.scrollControlBox}>
        <Tooltip title="Lên đầu trang">
          <Button className={classes.scrollBtn} onClick={handleScrollToTop}>
            <ExpandLessIcon />
          </Button>
        </Tooltip>

        <Tooltip title="Xuống cuối trang">
          <Button className={classes.scrollBtn} onClick={handleScrollToBottom}>
            <ExpandMoreIcon />
          </Button>
        </Tooltip>
      </Box>
    </Container>
  );
}
